import type { GetStaticPaths, GetStaticProps } from 'next';
import * as React from 'react';
import PageContainer from '@/components/PageContainer';
import config from '@/config/config';
import { FooterQuery, NavbarQuery, PageFieldsFragment, PageSectionsBySystemIdQuery } from '../../../@types/generated';
import { GraphQLError } from 'graphql';
import { createClient, notify, notifys } from '@/logging/bugnsag';
import { chunk } from '@/utils/arrays';
import {
  queryFooter,
  queryNavbar,
  queryPageBySlug,
  queryPageCollectionWithSlugsAndIds,
  querySectionsBySystemIds,
} from '@/clients/queries';
import { getOr } from '@/utils/options';
import { render } from '@/contentful';
import { apolloClient, getClient } from '@/utils/contentful';
import PreviewBanner from '@/components/PreviewBanner';
import { RadioSchedule } from '@/models/RadioTypes';
import { getRadioSchedules } from '@/clients/RadioUtils';

const bugsnagClient = createClient(process.env.BUGSNAG_API_KEY || '', process.env.NODE_ENV || 'develop');

type Props = {
  page: PageFieldsFragment;
  navbar: NavbarQuery;
  footer: FooterQuery;
  sections: Array<PageSectionsBySystemIdQuery>;
  radioSchedules: Record<string, RadioSchedule[]>;
  orderedIds: Array<string>;
  preview: boolean;
};

const RouteComponent = ({ page, navbar, footer, sections, orderedIds, preview, radioSchedules }: Props) => {
  return (
    <>
      {preview && <PreviewBanner />}
      <PageContainer
        title={getOr(page.title, 'Sýn')}
        metaTags={page.metaTags}
        contactFormSetting={page.contactFormSetting}
        navbar={navbar}
        footer={footer}
        radioSchedules={radioSchedules}
      >
        {render(sections, orderedIds)}
      </PageContainer>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const inPreview = getOr(context.draftMode, false);

  try {
    const client = getClient(inPreview);

    const slug = (context.params?.slugs ? [context.params.slugs].flat() : []).join('/');
    const contentfulPage = (await queryPageBySlug(client, slug, inPreview)).data;

    if (!contentfulPage.pageCollection) {
      throw new Error(`no page collection found in graphql response when fetching page details for ${slug}`);
    }

    if (!contentfulPage.pageCollection.items) {
      throw new Error(`no items array set in page details for ${slug}`);
    }

    const page = contentfulPage.pageCollection.items[0] || undefined;

    if (!page) {
      throw new Error(`no page component found in graphql response for slug ${slug}`);
    }

    const sectionIds = (page.sectionsCollection?.items.map((x) => x?.sys.id) || []) as string[];

    const chunkedSections = chunk(sectionIds, config.SECTION_CHUNK_SIZE).map((c) => {
      return querySectionsBySystemIds(client, c, inPreview).then((x) => x.data);
    });

    return {
      props: {
        navbar: (await queryNavbar(client)).data,
        footer: (await queryFooter(client)).data,
        page,
        sections: await Promise.all(chunkedSections),
        orderedIds: sectionIds,
        preview: inPreview,
        radioSchedules: await getRadioSchedules(),
      },
    };
  } catch (err) {
    notify(bugsnagClient, err as Error);
    throw err;
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const res = await queryPageCollectionWithSlugsAndIds(apolloClient, 0, 1000);
  if (res.error) {
    notify(bugsnagClient, res.error);
    throw res.error;
  }

  if (res.errors) {
    notifys(
      bugsnagClient,
      res.errors.map((error) => new GraphQLError(error.message))
    );
    throw new Error(`error when performing graphql query: ${res.errors.map((x) => x.toString()).join('\n')}`);
  }

  if (!res.data.pageCollection) {
    notify(bugsnagClient, new Error('unable to find page collection in response'));
    throw new Error('unable to find page collection in response');
  }
  const slugs = res.data.pageCollection.items.flatMap((x) =>
    x?.slug && x.slug !== '/' ? [x.slug.startsWith('/') ? x.slug.substring(1) : x.slug] : []
  );

  return {
    paths: slugs.map((x) => {
      return {
        params: { slugs: x.split('/') },
      };
    }),
    fallback: false,
  };
};

export default RouteComponent;
